import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Plus, SlidersHorizontal, Trash2, Pencil, ExternalLink } from 'lucide-react';

import { useDataState } from '@/hooks/use-data-state';
import {
  archiveWatchlistEntry,
  fetchActiveStrategiesWithParams,
  fetchActiveTradePlans,
  fetchPositions,
  fetchWatchlistEntries,
} from '@/lib/queries';
import { fetchDividendSummaries, type DividendSummary } from '@/lib/dividends';
import { fetchAIResearchMany, type AIResearchData } from '@/lib/ai-research';
// W64 — direct supabase access for return_13w_pct batch load
import { supabase } from '@/lib/supabase';
import { fetchSentimentAlertsBatch, type SymbolDriftAlert } from '@/lib/ai-research-history';
// Weltblick-Verdichtung (03.07.): QuintileBadge + HypeBadge aus der Zeile
// entfernt (Neben-Scores; Komponenten bleiben im Repo).
import { WatchlistBadgeLegend } from '@/components/watchlist/WatchlistBadgeLegend';
// W76 — Crypto-Macro-Hero (Halving, Fear-Greed, Funding)
import { CryptoMacroHeroCard } from '@/components/symbol/CryptoMacroHeroCard';
import { istBeachtenswert } from '@/hooks/use-zone-state';
import { zustandSortValue } from '@/lib/zustand-sortierung';
import type { SetupState } from '@/lib/v6/setup-state';
// W101 — Radar-Buckets (Watchlist nach Nähe zur Handelbarkeit)
import {
  classifyRadarBucket,
  radarSortValue,
  RADAR_BUCKET_META,
  type RadarBucket,
} from '@/lib/radar-bucket';
import { fetchChartEventsAll } from '@/lib/chart-events-read';
import { fetchCryptoBodenAll } from '@/lib/crypto-boden-read';
import { fetchReversalRadarFromDb } from '@/lib/reversal-radar';
// V7-B — Experten-Radar: off-watchlist-Nennungen deiner YouTube-Experten
import { ExpertRadarCard } from '@/components/watchlist/ExpertRadarCard';
import { EntdecktCard } from '@/components/watchlist/EntdecktCard';
// V7-A — Große Sicht (Intermarket-Regime: Dollar/Gold/Öl/Zinsen im Konsens)
import { IntermarketCard } from '@/components/dashboard/IntermarketCard';
import { StatusDot } from '@/components/ui/StatusDot';
import { DataStateView } from '@/components/ui/data-state-view';
import { AddSymbolDialog } from '@/components/watchlist/AddSymbolDialog';
// W70 Phase 3 — Demo-Mode-Check
import { useDemoMode } from '@/contexts/DemoModeContext';
import { SymbolQuoteCell } from '@/components/watchlist/SymbolQuoteCell';
import { ZoneStateHeadline } from '@/components/watchlist/ZoneStateHeadline';
import { KaufzonenBalken } from '@/components/watchlist/KaufzonenBalken';
// M19 — Score-Δ-7d Badge
import { ChartEventBadge } from '@/components/watchlist/ChartEventBadge';
import { PositionMgmtBlock } from '@/components/watchlist/PositionMgmtBlock';
import { DividendBadge } from '@/components/watchlist/DividendBadge';
import { MarketRegimeBadge } from '@/components/MarketRegimeBadge';
import { MarketPhasePill } from '@/components/dashboard/MarketPhasePill';
import { useMarketRegime } from '@/hooks/use-market-regime';
import { buildRealMarketPhaseInput } from '@/lib/v51/build-features';
// W67 Phase 4 — StrategyWithParameterSet ungenutzt nach SymbolScoreCell-prop-Removal.
import { cn } from '@/lib/utils';
import { getMarketDataProvider } from '@/lib/market-data';
// W88 P5 v2 (2026-05-27) — Batch-Prefetch durch DataLoader in cached-provider.ts
// ersetzt (siehe useEffect-Kommentar weiter unten).
import type { AssetClass, Position, WatchlistEntry, WatchlistLayer } from '@/types';

interface LayerDef {
  layer: WatchlistLayer;
  label: string;
  description: string;
  default_asset_class: AssetClass;
}

// V5.1 Layer-Definitionen (W67 Phase 4 — V5-Variante getilgt, da useEngineVersion stets 'v51')
//
// „Fokus" (20.09.2026) — Tageskadenz statt Themen-Gruppe: offene Positionen
// (unabhängig von ihrer Rolle) ∪ Rolle „tactical" (aktive Setups). Das sind
// die Werte, die heute einen Blick brauchen; core/theme/crypto/bestand
// warten wöchentlich bis monatlich (Ronni, 20.09.2026). Deshalb steht der
// Tab zuerst UND ist der neue Default (siehe defaultLayerForVersion unten).
const LAYERS_V51: readonly LayerDef[] = [
  {
    layer: 'fokus',
    label: 'Fokus',
    description: 'Offene Positionen und aktive Trade-Setups — der tägliche Blick.',
    default_asset_class: 'stock',
  },
  {
    layer: 'core',
    label: 'Core',
    description: 'Quality + Dividenden-Aristokraten + Index-ETFs für langfristigen Vermögensaufbau.',
    default_asset_class: 'stock',
  },
  {
    layer: 'tactical',
    label: 'Tactical',
    description: 'Aktive Trade-Setups: Breakout, Pullback, RS, Post-Earnings.',
    default_asset_class: 'stock',
  },
  {
    layer: 'theme',
    label: 'Theme',
    description: 'KI / Robotik / Space / Tesla — Themenwerte mit erhöhtem Risikobudget.',
    default_asset_class: 'stock',
  },
  {
    layer: 'defensive',
    label: 'Defensive',
    description: 'Gold / Edelmetalle / defensive Sektoren — Risikoausgleich in Bear-Phasen.',
    default_asset_class: 'commodity_etf',
  },
  {
    layer: 'crypto',
    label: 'Krypto',
    description: 'Bitcoin / Ethereum / Top-Alts — separat von Aktien-Logik.',
    default_asset_class: 'crypto',
  },
] as const;

type SortKey =
  | 'zustand'
  | 'radar'
  | 'name_asc'
  | 'name_desc'
  | 'added_desc';

const SORT_STORAGE_KEY = 'cockpit.watchlist.sort';

function loadSort(): SortKey {
  if (typeof window === 'undefined') return 'zustand';
  try {
    const v = window.localStorage.getItem(SORT_STORAGE_KEY);
    if (
      v === 'zustand' ||
      v === 'radar' ||
      v === 'name_asc' ||
      v === 'name_desc' ||
      v === 'added_desc'
    ) {
      return v;
    }
    // Stufe 1 der Score-Entkopplung (14.08.2026): Wer bisher nach Score
    // sortierte, landet auf der Zustand-Sortierung — nicht auf einem Fehler.
    if (v === 'score_desc' || v === 'score_asc') return 'zustand';
  } catch {
    // ignore
  }
  return 'zustand';
}

const SORT_LABEL: Record<SortKey, string> = {
  zustand: 'Zustand (Regel)',
  radar: 'Radar (Aufmerksamkeit)',
  name_asc: 'Name A-Z',
  name_desc: 'Name Z-A',
  added_desc: 'Neueste zuerst',
};

// DASH-LINKS 08-05 + V5.1: Whitelist gültiger Layer-Werte aus URL.
const VALID_LAYERS: WatchlistLayer[] = [
  // V5
  'swing', 'longterm_build', 'dividend', 'crypto_accumulation', 'sector_etf', 'open_position', 'day_swing',
  // V5.1
  'core', 'tactical', 'theme', 'defensive', 'crypto',
  // Tageskadenz (20.09.2026)
  'fokus',
];

function readLayerFromQuery(params: URLSearchParams, defaultLayer: WatchlistLayer): WatchlistLayer {
  const raw = params.get('layer');
  if (raw && (VALID_LAYERS as string[]).includes(raw)) {
    return raw as WatchlistLayer;
  }
  return defaultLayer;
}

export default function Watchlist() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isDemoMode } = useDemoMode();
  // V5.1 — Layer-Set abhängig von engine_version aus user_settings
  // W67 Phase 4 — useEngineVersion entfernt (immer 'v51').
  const LAYERS = LAYERS_V51;
  // 20.09.2026 — Fokus statt Core als Landing-Tab: der tägliche Blick zuerst,
  // nicht die monatliche Kategorie. Wer „Core" will, klickt bewusst hin.
  const defaultLayerForVersion: WatchlistLayer = 'fokus';
  const [activeLayer, setActiveLayer] = useState<WatchlistLayer>(() =>
    readLayerFromQuery(searchParams, defaultLayerForVersion)
  );

  // Wenn der User später ohne Reload zwischen Tabs in der App navigiert (z.B.
  // klick auf einen anderen Layer im Dashboard), URL-Param updaten.
  useEffect(() => {
    const fromUrl = readLayerFromQuery(searchParams, defaultLayerForVersion);
    if (fromUrl !== activeLayer) {
      setActiveLayer(fromUrl);
    }
    // searchParams bewusst als Dependency, sonst greift das nicht beim Browser-Back.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  // Beim Layer-Wechsel im Tab-UI auch die URL spiegeln, damit Bookmarks und
  // Browser-Back konsistent funktionieren.
  const updateActiveLayer = useCallback(
    (next: WatchlistLayer) => {
      setActiveLayer(next);
      const newParams = new URLSearchParams(searchParams);
      if (next === defaultLayerForVersion) {
        newParams.delete('layer');
      } else {
        newParams.set('layer', next);
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<WatchlistEntry | null>(null);
  // Audit 15.06.: Symbol aus dem Experten-Radar in den Add-Dialog vorausfüllen.
  const [prefillSymbol, setPrefillSymbol] = useState<string | null>(null);
  // Audit 13.07.: Entdeckt-Übernahme wählt Layer/Asset-Klasse passend zur
  // Gruppe vor (null = aktiven Tab erben, wie beim Experten-Radar).
  const [prefillLayer, setPrefillLayer] = useState<string | null>(null);
  const [prefillAssetClass, setPrefillAssetClass] = useState<string | null>(null);
  // W86 — Mobile: Header-Block (Subtitle + Filter + Markt-Badges) collapsible.
  // Default geschlossen damit mehr Scroll-Platz für die eigentliche Watchlist.
  // Auf Desktop (>=sm) immer expanded via CSS.
  const [mobileHeaderExpanded, setMobileHeaderExpanded] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>(() => loadSort());
  // Stufe 1 der Score-Entkopplung (14.08.2026): Die Score-Zelle ist aus der
  // Zeile genommen, im Browser wird kein Score mehr gerechnet. Der Cache
  // bleibt als (leerer) Radar-Eingang stehen, damit der Radar unveraendert
  // funktioniert — sein Score-Kriterium faellt damit bewusst still: Er lebt
  // von den Cron-Signalen (Chart-Ereignisse, Boeden, Wende-Zeichen).
  const [scoreCache] = useState<Map<string, number>>(() => new Map());
  // Audit 08.08.2026 — Zonen-Cache ersetzt den Kauf-Ampel-Cache.
  // Der „Nur Kaufbares"-Filter lief bisher auf der Kauf-Ampel (Score-basiert),
  // während die Zeile den Zustandsmaschinen-Zustand (Preis-basiert) anzeigte.
  // Zwei Urteile zur selben Frage. Jetzt liest der Filter denselben Zustand,
  // den die Zeile zeigt. `null` + `isLoading` werden getrennt geführt, damit
  // noch rechnende Zellen sichtbar bleiben (Cold-Start-Gnade).
  const [zoneCache, setZoneCache] = useState<
    Map<string, { state: SetupState | null; isLoading: boolean }>
  >(() => new Map());
  const reportZoneState = useCallback(
    (entryId: string, state: SetupState | null, isLoading: boolean) => {
      setZoneCache((prev) => {
        const cur = prev.get(entryId);
        if (cur && cur.state === state && cur.isLoading === isLoading) return prev;
        const next = new Map(prev);
        next.set(entryId, { state, isLoading });
        return next;
      });
    },
    []
  );

  // W49 — Focus-Mode: zeigt nur Symbole mit Conviction >= 75 (tier 'conviction_buy')
  const [focusMode, setFocusMode] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem('cockpit.watchlist.focusMode') === '1';
    } catch {
      return false;
    }
  });
  const toggleFocusMode = useCallback(() => {
    setFocusMode((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem('cockpit.watchlist.focusMode', next ? '1' : '0');
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  // W64 — Power-Filter: erweitert Focus-Mode um zusaetzliche Filter-Modi.
  // Mutually-exclusive (eines aktiv oder 'all'). Speichert in localStorage.
  // Stufe 1 der Score-Entkopplung: 'textbook' entfernt — er filterte auf dem
  // Conviction-Meta-Score, der nicht mehr gerechnet wird.
  type PowerFilterMode = 'all' | 'high_rs' | 'news_spike';
  const [powerFilter, setPowerFilter] = useState<PowerFilterMode>(() => {
    try {
      const v = window.localStorage.getItem('cockpit.watchlist.powerFilter');
      if (v === 'high_rs' || v === 'news_spike') return v;
      return 'all';
    } catch {
      return 'all';
    }
  });
  const setPowerFilterPersist = useCallback((m: PowerFilterMode) => {
    setPowerFilter(m);
    try {
      window.localStorage.setItem('cockpit.watchlist.powerFilter', m);
    } catch {
      // ignore
    }
  }, []);
  const queryClient = useQueryClient();

  const handleSortChange = (key: SortKey) => {
    setSortKey(key);
    try {
      window.localStorage.setItem(SORT_STORAGE_KEY, key);
    } catch {
      // ignore
    }
  };

  const watchlistState = useDataState({
    queryKey: ['watchlist', 'all'],
    queryFn: fetchWatchlistEntries,
    ttlSeconds: 60,
  });

  // DIV.1: Dividenden-Summaries für alle Aktien/ETF-Symbole laden — eine Query.
  const dividendSymbols = useMemo(() => {
    const set = new Set<string>();
    for (const e of watchlistState.state.data ?? []) {
      if (e.asset_class === 'stock' || e.asset_class === 'etf') {
        set.add(e.symbol.toUpperCase());
      }
    }
    return Array.from(set).sort();
  }, [watchlistState.state.data]);
  const dividendSummariesState = useDataState({
    queryKey: ['dividend-summaries', dividendSymbols.join(',')],
    queryFn: (signal) => fetchDividendSummaries(dividendSymbols, signal),
    enabled: dividendSymbols.length > 0,
    ttlSeconds: 30 * 60,
  });
  const dividendsMap: Map<string, DividendSummary> =
    dividendSummariesState.state.data ?? new Map();

  // PPLX.2 — KI-Research-Map als Fallback für leere Provider-Fundamentals.
  // Wird einmal pro Watchlist-Render gefetcht und an SymbolScoreCell durchgereicht.
  const stockSymbols = useMemo(() => {
    const set = new Set<string>();
    for (const e of watchlistState.state.data ?? []) {
      if (e.asset_class === 'stock') set.add(e.symbol.toUpperCase());
    }
    return Array.from(set).sort();
  }, [watchlistState.state.data]);
  const aiResearchState = useDataState({
    queryKey: ['ai-research-map', stockSymbols.join(',')],
    queryFn: (signal) => fetchAIResearchMany(stockSymbols, signal),
    enabled: stockSymbols.length > 0,
    ttlSeconds: 30 * 60,
  });
  const aiResearchMap: Map<string, AIResearchData> =
    aiResearchState.state.data ?? new Map();

  // W64 — return_13w_pct fuer High-RS-Filter (laedt aus v5_market_data_cache)
  const return13wState = useDataState<Map<string, number>>({
    queryKey: ['watchlist-return-13w', stockSymbols.join(',')],
    queryFn: async () => {
      if (stockSymbols.length === 0) return new Map();
      const symbols = [...stockSymbols, 'SPY'];
      const { data } = await supabase
        .from('v5_market_data_cache')
        .select('symbol, quote_data')
        .in('symbol', symbols);
      const out = new Map<string, number>();
      for (const row of (data ?? []) as Array<{
        symbol: string;
        quote_data: { return_13w_pct?: number | null } | null;
      }>) {
        const v = row.quote_data?.return_13w_pct;
        if (typeof v === 'number') out.set(row.symbol.toUpperCase(), v);
      }
      return out;
    },
    enabled: stockSymbols.length > 0,
    ttlSeconds: 30 * 60,
  });
  const return13wMap: Map<string, number> = return13wState.state.data ?? new Map();
  const spyReturn13w = return13wMap.get('SPY') ?? null;

  // Welle 14e — Sentiment-Drift-Alerts: batch-fetch der letzten 7d Snapshots
  // damit jede Watchlist-Row weiß ob es einen relevanten Shift gab.
  const driftAlertsState = useDataState({
    queryKey: ['sentiment-drift-alerts', stockSymbols.join(',')],
    queryFn: (signal) => fetchSentimentAlertsBatch(stockSymbols, 7, signal),
    enabled: stockSymbols.length > 0,
    ttlSeconds: 15 * 60,
  });
  const driftAlertsMap: Map<string, SymbolDriftAlert> =
    driftAlertsState.state.data ?? new Map();

  // Stufe 1 der Score-Entkopplung: Score-Δ- und Hype-Bulk-Reads entfernt —
  // sie fuetterten ausschliesslich die gestrichenen Score-Badges.

  const strategiesState = useDataState({
    queryKey: ['strategies', 'active'],
    queryFn: fetchActiveStrategiesWithParams,
    ttlSeconds: 60 * 5,
  });

  // MM5: offene Positionen + aktive Pläne als Indikatoren in Watchlist
  const openPositions = useDataState({
    queryKey: ['positions', 'open'],
    queryFn: (signal) => fetchPositions('open', signal),
    ttlSeconds: 60,
  });
  const activePlans = useDataState({
    queryKey: ['trade-plans', 'active'],
    queryFn: fetchActiveTradePlans,
    ttlSeconds: 60,
  });

  const openSymbols = useMemo(() => {
    const set = new Set<string>();
    for (const p of openPositions.state.data ?? []) set.add(p.symbol.toUpperCase());
    return set;
  }, [openPositions.state.data]);

  // OO2: Position pro Symbol nachschlagen, damit der Mgmt-Block sie nutzen kann.
  const positionBySymbol = useMemo(() => {
    const map = new Map<string, Position>();
    for (const p of openPositions.state.data ?? []) {
      map.set(p.symbol.toUpperCase(), p);
    }
    return map;
  }, [openPositions.state.data]);
  const planSymbols = useMemo(() => {
    const set = new Set<string>();
    const wl = watchlistState.state.data ?? [];
    const wlById = new Map(wl.map((w) => [w.id, w.symbol.toUpperCase()]));
    for (const p of activePlans.state.data ?? []) {
      if (p.status === 'triggered') continue;
      const sym = wlById.get(p.watchlist_entry_id);
      if (sym) set.add(sym);
    }
    return set;
  }, [activePlans.state.data, watchlistState.state.data]);

  // W101 — Radar-Buckets: die DETERMINISTISCHEN Cron-Signale (18:15-Lauf)
  // bestimmen die Bucket-Zugehörigkeit; der Browser-Score ist nur
  // Tie-Breaker INNERHALB eines Buckets → keine springenden Zeilen.
  // Nur geladen, wenn der Radar-Sort aktiv ist (sonst kein Extra-Call).
  const radarActive = sortKey === 'radar';
  const chartEventsAll = useDataState({
    queryKey: ['chart-events-all'],
    queryFn: (signal) => fetchChartEventsAll(signal),
    enabled: radarActive,
    ttlSeconds: 30 * 60,
    isEmpty: (m) => !m || m.size === 0,
  });
  const cryptoBodenAll = useDataState({
    queryKey: ['crypto-boden-all'],
    queryFn: (signal) => fetchCryptoBodenAll(signal),
    enabled: radarActive,
    ttlSeconds: 30 * 60,
    isEmpty: (m) => !m || m.size === 0,
  });
  const reversalRadarAll = useDataState({
    queryKey: ['reversal-radar-db'],
    queryFn: (signal) => fetchReversalRadarFromDb(signal),
    enabled: radarActive,
    ttlSeconds: 30 * 60,
    isEmpty: (r) => !r || r.length === 0,
  });
  const stabilizingSymbols = useMemo(() => {
    const set = new Set<string>();
    for (const r of reversalRadarAll.state.data ?? []) {
      if (r.stabilizing) set.add(r.symbol.toUpperCase());
    }
    return set;
  }, [reversalRadarAll.state.data]);

  const radarFor = useCallback(
    (entry: WatchlistEntry) => {
      const sym = entry.symbol.toUpperCase();
      const boden = cryptoBodenAll.state.data?.get(sym) ?? null;
      const topEvent = chartEventsAll.state.data?.get(sym)?.top_event ?? null;
      // Alter des Ausbruchs (für das Frische-Gate im Radar): ein 3-Monate-alter
      // „W-Boden" ist kein „heute handelbar" mehr.
      const occurredAt =
        (topEvent as unknown as { occurred_at?: string | null } | null)?.occurred_at ?? undefined;
      const ageMs = occurredAt ? Date.now() - new Date(occurredAt).getTime() : NaN;
      const chartEventAgeDays = Number.isFinite(ageMs) ? Math.round(ageMs / 86_400_000) : null;
      return classifyRadarBucket({
        score: scoreCache.get(entry.id) ?? null,
        cryptoT1Ready: boden?.t1_ready === true,
        bodenScore: boden?.boden_score ?? null,
        stabilizing: stabilizingSymbols.has(sym),
        hasActivePlan: planSymbols.has(sym),
        chartTone: topEvent?.tone ?? null,
        chartKind: topEvent?.kind ?? null,
        chartEventAgeDays,
      });
    },
    [
      cryptoBodenAll.state.data,
      chartEventsAll.state.data,
      stabilizingSymbols,
      planSymbols,
      scoreCache,
    ]
  );

  const provider = useMemo(() => getMarketDataProvider(), []);
  const regime = useMarketRegime();

  // Welle 13 (Decision 4) — echte SPY/VIX-Marktphase EINMAL pro Watchlist-Render
  // berechnen und an alle Cells durchreichen. Vorher nutzte jede Cell
  // defaultMarketPhase()='neutral', der Marktfilter war faktisch tot. Bei
  // fehlenden SPY/VIX-Daten liefert computeMarketPhase ohnehin 'neutral' →
  // sicherer Fallback, kein N×-Fetch. marketPhaseInput wird mit der Pill geteilt
  // (eine Quelle für Anzeige UND Scoring).
  const marketPhaseInput = useMemo(
    () =>
      buildRealMarketPhaseInput({
        spyPrice: regime.spyPrice,
        spyFeatures: regime.spyFeatures,
        vix: regime.vix,
        sectorRotation: regime.sectorRotation,
      }),
    [regime.spyPrice, regime.spyFeatures, regime.vix, regime.sectorRotation]
  );

  // W88 P5 v2 (2026-05-27) — Batch-Prefetch wurde durch DataLoader-Pattern in
  // cached-provider.ts ersetzt. Vorteil: keine Race-Condition (Cells starten
  // ihre useQuery sofort bei Mount, useEffect fired erst danach → Cells
  // fetchten individuell BEVOR Prefetch RPC kam). Jetzt batcht der Provider
  // selbst alle konkurrenten getOhlcv(daily,<=250)-Calls in 1 RPC pro 10ms-
  // Window → 60 cells = 1 Roundtrip ohne UI-Code-Change.

  const counts = useMemo(() => {
    const m = new Map<WatchlistLayer, number>();
    const items = watchlistState.state.data;
    let fokusCount = 0;
    if (items) {
      items.forEach((e: WatchlistEntry) => {
        // V5.1-FIX: im V5.1-Modus zählen wir nach watchlist_role, sonst nach layer
        const key = e.watchlist_role as WatchlistLayer | undefined;
        if (key) m.set(key, (m.get(key) ?? 0) + 1);
        // Fokus = offene Position ODER Rolle tactical — dieselbe Regel wie
        // im Filter unten, hier nur gezaehlt statt gefiltert.
        if (openSymbols.has(e.symbol.toUpperCase()) || e.watchlist_role === 'tactical') {
          fokusCount++;
        }
      });
    }
    // MM5: open_position-Layer aus tatsächlichen offenen Positions zählen,
    // nicht aus den Watchlist-Einträgen (die haben oft layer != 'open_position').
    m.set('open_position', openSymbols.size);
    m.set('fokus', fokusCount);
    return m;
  }, [watchlistState.state.data, openSymbols]);

  const activeLayerDef = LAYERS.find((l) => l.layer === activeLayer) ?? LAYERS[0]!;
  // „Fokus" ist eine Sicht (offene Position ∪ tactical), keine speicherbare
  // Rolle. Neue/bearbeitete Einträge brauchen eine ECHTE Rolle als Vorschlag
  // — sonst würde 'fokus' selbst als watchlist_role gespeichert.
  const speicherbareRolleFuerNeueintrag: WatchlistLayer =
    activeLayer === 'fokus' ? 'tactical' : activeLayer;

  const refetchAll = () => {
    void queryClient.invalidateQueries({ queryKey: ['watchlist'] });
  };

  const handleArchive = async (id: string) => {
    await archiveWatchlistEntry(id);
    refetchAll();
  };

  return (
    <div className="flex flex-col h-[100dvh] lg:h-screen">
      {/* Header */}
      <header className="px-3 py-2 sm:px-6 sm:py-4 border-b border-zinc-800/60">
        {/* W86 Mobile-Fix — Title + Add-Btn + Filter-Toggle always visible.
            Subtitle nur Desktop (Mobile spart Platz). Filter/Markt-Block
            collapsible auf Mobile (default closed). */}
        <div className="flex items-center sm:items-baseline justify-between gap-2 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl font-semibold leading-tight">Watchlist</h1>
            <p className="hidden sm:block text-sm text-zinc-400 mt-0.5">
              {provider.is_live
                ? `Marktdaten: ${provider.name}`
                : 'Stille Phase — Marktdaten kommen in Phase 2.3 / 3 (IBKR).'}
            </p>
          </div>
          {/* Mobile-only: Filter-Toggle (öffnet/schließt Filter+Markt-Block) */}
          <button
            type="button"
            onClick={() => setMobileHeaderExpanded((v) => !v)}
            aria-expanded={mobileHeaderExpanded}
            aria-label={mobileHeaderExpanded ? 'Filter schliessen' : 'Filter & Markt-Info anzeigen'}
            className="sm:hidden inline-flex items-center gap-1.5 rounded-md border border-cockpit-border bg-cockpit-surface2 hover:bg-cockpit-surface3 px-2 py-1.5 text-[11px] font-mono text-zinc-300 focus-ring shrink-0"
          >
            <SlidersHorizontal className="size-3" />
            <ChevronDown className={cn('size-3 transition-transform', mobileHeaderExpanded && 'rotate-180')} />
          </button>
          {!isDemoMode && (
            <button
              type="button"
              onClick={() => setAddDialogOpen(true)}
              aria-label="Symbol hinzufügen"
              className="inline-flex items-center gap-1.5 rounded-md bg-cockpit-accent hover:bg-cockpit-accent/85 text-white text-sm font-medium px-2.5 sm:px-3 py-1.5 sm:py-2 min-h-[36px] focus-ring shrink-0"
            >
              <Plus className="size-4" />
              <span className="hidden sm:inline">Symbol hinzufügen</span>
            </button>
          )}
        </div>

        {/* Filter-Block — Mobile: nur sichtbar wenn expanded. Desktop: immer.
            Beinhaltet: Focus-Toggle + 4 Power-Filter + Sort-Dropdown */}
        <div className={cn(
          'mt-3 items-center gap-2 flex-wrap',
          mobileHeaderExpanded ? 'flex' : 'hidden',
          'sm:flex'
        )}>
            {/* W49 — Focus-Mode-Toggle: blendet alle ausser Conviction-Picks aus */}
            <button
              type="button"
              onClick={toggleFocusMode}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-2 text-xs font-semibold focus-ring transition-colors',
                focusMode
                  ? 'bg-emerald-500/15 border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/25'
                  : 'bg-cockpit-surface2 border-cockpit-border text-zinc-400 hover:text-zinc-200'
              )}
              title={
                focusMode
                  ? 'Nur Kaufbares ist AN — nur Werte mit 🟢 KAUFBAR oder 🟡 WARTEN werden gezeigt. Klick um alle zu sehen.'
                  : 'Nur Kaufbares aktivieren — blendet alles aus, was gerade keine Handlung braucht (⚪/🔴).'
              }
            >
              {focusMode ? '🟢🟡 Nur Kaufbares' : 'Alle Werte'}
            </button>
            {/* W64 — Power-Filter-Chips */}
            {(['all', 'high_rs', 'news_spike'] as const).map((m) => {
              const labels = {
                all: 'Alle',
                high_rs: 'High-RS',
                news_spike: 'News-Spike',
              };
              const hints = {
                all: 'Keine zusaetzliche Filterung',
                high_rs: 'Nur Symbole mit return_13w_pct > +10pp ueber SPY',
                news_spike: 'Nur Symbole mit ≥10 News-Headlines in den letzten 7 Tagen',
              };
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setPowerFilterPersist(m)}
                  title={hints[m]}
                  className={cn(
                    'inline-flex items-center rounded-md border px-2 py-1.5 text-[11px] font-mono focus-ring transition-colors',
                    powerFilter === m
                      ? 'bg-violet-500/15 border-violet-500/50 text-violet-300'
                      : 'bg-cockpit-surface2 border-cockpit-border text-zinc-500 hover:text-zinc-200'
                  )}
                >
                  {labels[m]}
                </button>
              );
            })}
            {/* Audit GG2: Sortier-Dropdown */}
            <select
              value={sortKey}
              onChange={(e) => handleSortChange(e.target.value as SortKey)}
              className="rounded-md bg-cockpit-surface2 border border-cockpit-border px-2 py-2 text-xs font-mono focus-ring"
              title="Watchlist sortieren"
            >
              {(Object.keys(SORT_LABEL) as SortKey[]).map((k) => (
                <option key={k} value={k}>
                  {SORT_LABEL[k]}
                </option>
              ))}
            </select>
        </div>

        {/* Marktregime-Badges + V5.1-Marktphase — Mobile: nur wenn expanded */}
        {provider.is_live && (
          <div className={cn(
            'mt-3 flex-wrap items-center gap-2',
            mobileHeaderExpanded ? 'flex' : 'hidden',
            'sm:flex'
          )}>
            <MarketRegimeBadge regime={regime.stocks} label="Aktien / ETF" />
            <MarketRegimeBadge regime={regime.crypto} label="Krypto" />
            <MarketPhasePill input={marketPhaseInput} />
          </div>
        )}

        {/* Layer-Tabs — W81 Mobile-Fix: scroll-snap + Fade-Indicator rechts.
            Auf 375px sieht User sonst nur ~3 von 8 Tabs (Core/Tactical/Theme)
            und merkt nicht dass weitere Layer rechts versteckt sind.
            W86 — mt-2 auf Mobile (statt mt-4) für kompakteren Header. */}
        <div className="relative mt-2 sm:mt-4 -mb-px">
          <nav
            className="flex gap-1 overflow-x-auto snap-x snap-mandatory scrollbar-thin"
            role="tablist"
          >
          {LAYERS.map((l) => (
            <button
              key={l.layer}
              type="button"
              role="tab"
              aria-selected={l.layer === activeLayer}
              onClick={() => updateActiveLayer(l.layer)}
              className={cn(
                'snap-start shrink-0 rounded-t-md border-b-2 px-3 py-2 text-sm transition-colors focus-ring',
                l.layer === activeLayer
                  ? 'border-blue-500 text-blue-300 bg-zinc-900/40'
                  : 'border-transparent text-zinc-400 hover:text-zinc-200 hover:bg-zinc-900/20'
              )}
            >
              <span>{l.label}</span>
              <span
                className={cn(
                  'ml-1.5 inline-flex items-center justify-center rounded-full px-1.5 text-[10px] min-w-[1.25rem]',
                  l.layer === activeLayer
                    ? 'bg-blue-500/20 text-blue-200'
                    : 'bg-zinc-800 text-zinc-500'
                )}
              >
                {/* W74.7 — Skeleton statt "0" waehrend Loading
                    (Audit D: FOEC-Bug Counts springen 0→32 nach 1-2s). */}
                {watchlistState.state.status === 'loading' && !watchlistState.state.data
                  ? '·'
                  : (counts.get(l.layer) ?? 0)}
              </span>
            </button>
          ))}
          </nav>
          {/* W81 Mobile-Fix — rechts Fade-Gradient als Scroll-Hinweis. Macht
              visuell klar dass weitere Tabs rechts ausgeblendet sind. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-cockpit-base to-transparent sm:hidden"
          />
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-6">
        {/* W86 — Description nur auf Desktop sichtbar (spart Mobile-Höhe) */}
        <div className="hidden sm:block text-sm text-zinc-400 mb-4">{activeLayerDef.description}</div>

        {/* V7-A — Große Sicht (Intermarket-Regime) */}
        <div className="mb-4">
          <IntermarketCard />
        </div>

        {/* V7-B — Experten-Radar (off-watchlist), einklappbar */}
        <div className="mb-4 space-y-2">
          <ExpertRadarCard
            onAdd={(sym) => {
              setPrefillSymbol(sym);
              setAddDialogOpen(true);
            }}
          />
          {/* Stufe A „Entdecken" (07.07.) — Welt-Universum-Vorschläge des
              Nachtlaufs, gleiche 1-Klick-Übernahme wie das Experten-Radar. */}
          <EntdecktCard
            onAdd={(sym, prefill) => {
              setPrefillSymbol(sym);
              setPrefillLayer(prefill.layer);
              setPrefillAssetClass(prefill.assetClass);
              setAddDialogOpen(true);
            }}
          />
        </div>

        {/* W76 — Crypto-Macro-Hero auf Krypto-Tab (Halving + Fear-Greed + Funding) */}
        {activeLayer === 'crypto' && (
          <div className="mb-4">
            <CryptoMacroHeroCard />
          </div>
        )}

        {/* W47 — Badge-Legende (kollabierbar) — erklaert Q1-Q5, Hype, Score-Delta, Dividende */}
        <WatchlistBadgeLegend />

        <DataStateView
          state={watchlistState.state}
          onRetry={watchlistState.refetch}
          empty={{
            title: `Noch keine Einträge im Layer "${activeLayerDef.label}"`,
            description:
              'Füge dein erstes Symbol hinzu, damit Strategien darauf angewandt werden können.',
            action: (
              <button
                type="button"
                onClick={() => setAddDialogOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium px-3 py-2 focus-ring"
              >
                <Plus className="size-4" />
                Symbol hinzufügen
              </button>
            ),
          }}
        >
          {(items) => {
            const filtered = items
              .filter((it) => {
                // MM5: open_position-Tab zeigt alle Symbole mit offener Position
                // (unabhängig vom ursprünglichen Layer). Andere Tabs filtern wie gewohnt.
                if (activeLayer === 'open_position') {
                  return openSymbols.has(it.symbol.toUpperCase());
                }
                // Fokus (20.09.2026) — Tageskadenz: offene Position ODER
                // Rolle tactical, unabhängig davon, unter welcher Rolle das
                // Symbol sonst geführt wird. Dieselbe Regel wie in `counts`.
                if (activeLayer === 'fokus') {
                  return (
                    openSymbols.has(it.symbol.toUpperCase()) || it.watchlist_role === 'tactical'
                  );
                }
                // W67 Phase 4 — V5.1-only: nutzt watchlist_role (V5-Layer-Fallback entfernt).
                return it.watchlist_role === activeLayer;
              })
              .sort((a, b) => {
                switch (sortKey) {
                  case 'zustand': {
                    // Stufe 1 der Score-Entkopplung: Die Rangfolge kommt aus
                    // der Zustandsmaschine (Naehe zur Handlung + Bedingungs-
                    // Fortschritt) — dieselbe Quelle wie Zeile und Filter.
                    const va = zustandSortValue(zoneCache.get(a.id));
                    const vb = zustandSortValue(zoneCache.get(b.id));
                    if (va !== vb) return va - vb;
                    return a.symbol.localeCompare(b.symbol);
                  }
                  case 'radar': {
                    const va = radarSortValue(radarFor(a));
                    const vb = radarSortValue(radarFor(b));
                    if (va !== vb) return va - vb;
                    return a.symbol.localeCompare(b.symbol);
                  }
                  case 'name_asc':
                    return a.symbol.localeCompare(b.symbol);
                  case 'name_desc':
                    return b.symbol.localeCompare(a.symbol);
                  case 'added_desc':
                    return (
                      new Date(b.added_at).getTime() -
                      new Date(a.added_at).getTime()
                    );
                  default:
                    return 0;
                }
              });
            if (filtered.length === 0) {
              return (
                <div className="rounded-lg border border-zinc-800/80 bg-zinc-900/30 p-8 text-center">
                  <div className="text-sm font-medium text-zinc-200">
                    Keine Einträge im Layer „{activeLayerDef.label}"
                  </div>
                  <div className="text-xs text-zinc-400 mt-1">
                    Andere Layer haben Einträge. Klick oben auf einen anderen Tab oder füge ein
                    neues Symbol für diesen Layer hinzu.
                  </div>
                  <button
                    type="button"
                    onClick={() => setAddDialogOpen(true)}
                    className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium px-3 py-2 focus-ring"
                  >
                    <Plus className="size-4" />
                    Symbol hinzufügen
                  </button>
                </div>
              );
            }
            // Audit 08.08.2026 — Focus-Mode = „Nur Kaufbares" läuft jetzt über
            // die Zustandsmaschine statt über die Kauf-Ampel: `zone_offen`
            // (Handlung mit Preis) und `boden_reift` (Beobachtung mit
            // Trigger-Preis). Alles andere braucht heute keinen Blick.
            // Noch nicht berechnete Zeilen bleiben sichtbar (Cold-Start-Gnade).
            let focusFiltered = focusMode
              ? filtered.filter((entry) => {
                  const z = zoneCache.get(entry.id);
                  if (z === undefined || z.isLoading) return true; // rechnet noch
                  return istBeachtenswert(z.state);
                })
              : filtered;
            // W64 — Power-Filter zusaetzlich anwenden
            if (powerFilter !== 'all') {
              focusFiltered = focusFiltered.filter((entry) => {
                const sym = entry.symbol.toUpperCase();
                if (powerFilter === 'news_spike') {
                  const ai = aiResearchMap.get(sym);
                  return (ai?.fh_news_count_7d ?? 0) >= 10;
                }
                if (powerFilter === 'high_rs') {
                  if (spyReturn13w === null) return true; // gnade
                  const r = return13wMap.get(sym);
                  if (r === undefined) return false;
                  return r - spyReturn13w >= 10;
                }
                return true;
              });
            }
            return (
              <ul className="space-y-2">
                {focusMode && filtered.length > 0 && focusFiltered.length < filtered.length && (
                  <li className="rounded-md border border-emerald-500/40 bg-emerald-950/20 px-3 py-2 text-xs text-emerald-200">
                    <strong>Nur Kaufbares:</strong> {focusFiltered.length} von {filtered.length}{' '}
                    Werten gezeigt — offene Kaufzone oder reifender Boden. Der Rest steht
                    zu nah am Hoch, ist zu tief gefallen oder zeigt keine Wende. Die
                    Begründung steht als Zeile unter dem Ticker.
                  </li>
                )}
                {(() => {
                  // W101 — beim Radar-Sort vor jedem neuen Bucket eine
                  // Überschrift („Beobachten" / „Geduld").
                  //
                  // Der dritte Bucket „Jetzt handelbar" ist am 10.08.2026
                  // entfallen: Der Radar lenkt Aufmerksamkeit, er entscheidet
                  // nicht über Handelbarkeit. Das tut allein die
                  // Zustandsmaschine über `zone_offen` — dieselbe Quelle, aus
                  // der weiter unten auch der Fokus-Modus filtert. Vorher
                  // standen in dieser Tabelle zwei Autoritäten nebeneinander.
                  let lastBucket: RadarBucket | null = null;
                  return focusFiltered.map((entry) => {
                    const row = (
                      <WatchlistEntryRow
                        key={entry.id}
                        entry={entry}
                        onArchive={() => void handleArchive(entry.id)}
                        onEdit={() => setEditEntry(entry)}
                        onZoneStateComputed={reportZoneState}
                        hasOpenPosition={openSymbols.has(entry.symbol.toUpperCase())}
                        hasActivePlan={planSymbols.has(entry.symbol.toUpperCase())}
                        openPosition={positionBySymbol.get(entry.symbol.toUpperCase()) ?? null}
                        dividendSummary={dividendsMap.get(entry.symbol.toUpperCase()) ?? null}
                        aiResearch={aiResearchMap.get(entry.symbol.toUpperCase()) ?? null}
                        driftAlert={driftAlertsMap.get(entry.symbol.toUpperCase()) ?? null}
                      />
                    );
                    if (sortKey !== 'radar') return row;
                    const bucket = radarFor(entry).bucket;
                    if (bucket === lastBucket) return row;
                    lastBucket = bucket;
                    const meta = RADAR_BUCKET_META[bucket];
                    return (
                      <div key={`hdr-${bucket}`} className="contents">
                        <li
                          className="mt-1 first:mt-0 flex items-center gap-2 px-1 py-1.5"
                          title={meta.hint}
                        >
                          <StatusDot tone={meta.tone} />
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-300">
                            {meta.label}
                          </span>
                        </li>
                        {row}
                      </div>
                    );
                  });
                })()}
              </ul>
            );
          }}
        </DataStateView>
      </div>

      <AddSymbolDialog
        open={addDialogOpen}
        initialSymbol={prefillSymbol}
        onClose={() => {
          setAddDialogOpen(false);
          setPrefillSymbol(null);
          setPrefillLayer(null);
          setPrefillAssetClass(null);
        }}
        defaultLayer={(prefillLayer ?? speicherbareRolleFuerNeueintrag) as typeof activeLayer}
        defaultAssetClass={
          (prefillAssetClass ?? activeLayerDef.default_asset_class) as AssetClass
        }
        strategies={strategiesState.state.data?.map((s) => s.strategy) ?? []}
        onSaved={() => {
          setAddDialogOpen(false);
          setPrefillSymbol(null);
          setPrefillLayer(null);
          setPrefillAssetClass(null);
          refetchAll();
        }}
      />

      <AddSymbolDialog
        open={editEntry !== null}
        onClose={() => setEditEntry(null)}
        defaultLayer={editEntry?.layer ?? speicherbareRolleFuerNeueintrag}
        defaultAssetClass={editEntry?.asset_class ?? activeLayerDef.default_asset_class}
        strategies={strategiesState.state.data?.map((s) => s.strategy) ?? []}
        editEntry={editEntry}
        onSaved={() => {
          setEditEntry(null);
          refetchAll();
        }}
      />
    </div>
  );
}

function WatchlistEntryRow({
  entry,
  onArchive,
  onEdit,
  // W67 Phase 4 — strategyNames-Prop entfernt (war nur fuer V5-Legacy-Strategie-Badge-Anzeige)

  onZoneStateComputed,
  hasOpenPosition,
  hasActivePlan,
  openPosition,
  dividendSummary,
  aiResearch,
  // Weltblick-Verdichtung (03.07.): driftAlert/hype/rank kommen weiter als
  // Props an (Bulk-Reads der Page unverändert), werden in der Zeile aber
  // nicht mehr gerendert — Kern statt 19 Info-Cluster.

}: {
  entry: WatchlistEntry;
  onArchive: () => void;
  onEdit: () => void;
  /** Audit 08.08.2026 — Zustandsmaschinen-Zustand nach oben („Nur Kaufbares"-Filter). */
  onZoneStateComputed?: (entryId: string, state: SetupState | null, isLoading: boolean) => void;
  hasOpenPosition?: boolean;
  hasActivePlan?: boolean;
  openPosition?: Position | null;
  dividendSummary?: DividendSummary | null;
  aiResearch?: AIResearchData | null;
  driftAlert?: SymbolDriftAlert | null;
}) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const { isDemoMode, linkTo } = useDemoMode();
  const navigate = useNavigate();
  // W67 Phase 4 — useEngineVersion entfernt (immer 'v51').

  // Kaufzonen-Balken (23.09.2026) — derselbe Kurs, den SymbolQuoteCell ohnehin
  // schon für sich selbst abruft, nur zusätzlich für den Balken gemeldet.
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);

  return (
    <li className="rounded-md border border-zinc-800/80 bg-zinc-900/30 hover:bg-zinc-900/50 transition-colors p-4">
      {/* W81 Mobile-Fix — auf <sm wird der Row-Inhalt vertikal gestapelt:
          [Main-Info] / [Quote+Score+Badges+Actions]. Auf >=sm bleibt der
          klassische Desktop-Flow als einreihiges flex-row. */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-3 sm:gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() =>
                navigate(linkTo(`/symbol/${entry.layer}/${encodeURIComponent(entry.symbol)}`))
              }
              className="group inline-flex items-center gap-1.5 text-base font-semibold font-mono text-zinc-100 hover:text-blue-300 focus-ring rounded"
            >
              {entry.symbol}
              <ExternalLink className="size-3 text-zinc-500 group-hover:text-blue-400" />
            </button>
            {entry.display_name && entry.display_name !== entry.symbol && (
              <span className="text-sm text-zinc-400 truncate">— {entry.display_name}</span>
            )}
            {/* MM5: Position-/Plan-Badges */}
            {hasOpenPosition && (
              <span className="inline-flex items-center rounded border border-emerald-700/60 bg-emerald-900/50 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-emerald-200 shrink-0">
                Offen
              </span>
            )}
            {hasActivePlan && !hasOpenPosition && (
              <span className="inline-flex items-center rounded border border-blue-700/60 bg-blue-900/50 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-blue-200 shrink-0">
                Plan
              </span>
            )}
          </div>
          {/* Signal-Konzept Schritt 1 (07.08.): Zustand + Preis als Überschrift
              statt der Punktzahl — direkte Antwort auf „Kauf ich mit 74? Mit
              64?". Eigenständig von der Score-Engine, liest nur Preis/Struktur. */}
          <ZoneStateHeadline
            symbol={entry.symbol}
            assetClass={entry.asset_class}
            entryId={entry.id}
            onZoneStateComputed={onZoneStateComputed}
          />
          <KaufzonenBalken
            symbol={entry.symbol}
            assetClass={entry.asset_class}
            currentPrice={currentPrice}
          />
          {/* Weltblick-Verdichtung (03.07., Agent-Inventur: 19 Info-Cluster je
              Zeile!): Drift-Chip, Asset-Klasse/-Subtype, Theme-Tags, Exchange/
              Währung/Datum + #User-Tags raus aus der Zeile — alles im
              Symbol-Detail, wo die Entscheidung fällt. Die Zeile zeigt den KERN:
              Symbol · Status · Kurs · Score · Überzeugung · 1 Chart-Signal. */}
          {(dividendSummary || aiResearch) && (
            <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] text-zinc-500">
              <DividendBadge
                summary={dividendSummary ?? null}
                aiResearch={aiResearch}
                compact
              />
            </div>
          )}

          {/* Welle 7: applicable_strategies enthält bei V4-Imports
              Platzhalter-UUIDs (00000000-...) die in V5.1 keinen Sinn ergeben.
              W67 Phase 4 — V5-Strategie-Anzeige entfernt (V5.1-Multi-Score-Engine
              in der Score-Cell zeigt die gewinnende Strategie). */}

          {entry.notes && (
            <div className="mt-2 text-xs text-zinc-400 italic line-clamp-2">{entry.notes}</div>
          )}

          {/* Weltblick-Verdichtung (03.07.): #User-Tags aus der Zeile entfernt
              (im Symbol-Detail sichtbar) — Kern statt Einzelpunkte. */}

          {/* W97 — OverboughtWarning gelöscht: das 🔥-Veto am Score (overextension.ts)
              ist die EINE Überhitzungs-Wahrheit; Hinweis steckt in dessen Tooltip. */}
        </div>

        {/* W81 Mobile-Fix — rechter Block: Quote + Score + Mini-Badges + Actions.
            Auf <sm horizontal mit flex-wrap unter Main-Info. Auf >=sm Teil des
            originalen Desktop-flex-row. */}
        <div className="flex flex-row flex-wrap sm:flex-nowrap items-start gap-3 sm:gap-4 justify-end sm:justify-start">
        {/* Quote (live oder still — Cell entscheidet) */}
        <SymbolQuoteCell symbol={entry.symbol} onQuoteComputed={setCurrentPrice} />

        {/* Stufe 1 der Score-Entkopplung (14.08.2026): Die Score-Zelle ist aus
            der Zeile genommen. Der Zustand (ZoneStateHeadline) traegt die
            Aussage; der Score behauptete eine Rangfolge, die im Sperrzeitraum
            nicht standhielt (konzepte/2026-08-08-vorregistrierte-signalpruefung.md).
            Die Komponente bleibt im Code — Stufe 2 raeumt die Pipeline auf,
            wenn sich diese Ansicht bewaehrt. */}
        {/* W69 Phase 3 — Mini-Badges in eigener Spalte:
            vertikal mittig zum Score-Block, einheitliches gap-1.5,
            flex-col bei vielen Badges damit nichts ueberlaeuft.
            W81 Mobile-Fix — auf <sm horizontal arrangiert (flex-row), damit
            Vertikal-Platz gespart wird. */}
        <div className="flex flex-row flex-wrap sm:flex-col items-center sm:items-end gap-1.5 shrink-0 min-w-[3rem] pt-0 sm:pt-4">
          {/* Stufe 1 der Score-Entkopplung: Conviction- und Score-Δ-Badge
              entfernt — Meta-Scores auf einem Score, der nichts vorhersagt,
              sind doppelte Dekoration. */}
          {/* W93 Phase 3 — wichtigstes Chart-Ereignis (Server-Cron 18:15) */}
          <ChartEventBadge symbol={entry.symbol} />
          {/* Weltblick-Verdichtung (03.07.): Quintile- + Hype-Badge raus aus
              der Zeile — Neben-Scores verwässern die eine Wahrheit; Details
              im Symbol-Detail. */}
        </div>

        {/* Actions — im Demo-Mode ausgeblendet */}
        {!isDemoMode && (
          <div className="flex flex-col gap-1 shrink-0">
            <button
              type="button"
              title="Bearbeiten"
              onClick={onEdit}
              className="rounded-md p-1.5 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 focus-ring"
            >
              <Pencil className="size-4" />
            </button>
            {confirmingDelete ? (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={onArchive}
                  className="rounded-md bg-red-600 hover:bg-red-500 text-white text-[10px] font-medium px-2 py-1 focus-ring"
                >
                  Wirklich
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(false)}
                  className="rounded-md border border-zinc-700 text-zinc-300 text-[10px] px-2 py-1 hover:bg-zinc-800 focus-ring"
                >
                  Abbruch
                </button>
              </div>
            ) : (
              <button
                type="button"
                title="Symbol entfernen"
                onClick={() => setConfirmingDelete(true)}
                className="rounded-md p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-950/30 focus-ring"
              >
                <Trash2 className="size-4" />
              </button>
            )}
          </div>
        )}
        </div>{/* /right-side block (W81 Mobile-Fix) */}
      </div>

      {/* OO2 — Management-Block für offene Positionen */}
      {hasOpenPosition && openPosition && (
        <PositionMgmtBlock position={openPosition} />
      )}
    </li>
  );
}

// Weltblick-Verdichtung (03.07.): labelForAssetClass/labelForSubtype/
// labelForThemeTag/formatDate entfernt — die Meta-Badges sind aus der
// Zeile verschwunden (Kern statt 19 Info-Cluster; Details im Symbol-Detail).
